import React, { useState, useCallback, useEffect } from 'react';
import { Alert, Box, Button, CircularProgress, SelectChangeEvent, Typography } from '@mui/material';
import {
  AddOutlined as AddIcon,
  FileDownloadOutlined as DownloadIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageToolbar,
  ListPageFiltersSection,
} from '../../common/list-page';
import CommonPagination from '../../common/Pagination';
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  StyledSnackbar,
  StyledAlert,
} from '../../products/ProductCatalog/ProductCatalog.styles';
import { CATALOG_CTA_GRADIENT, CATALOG_CTA_GRADIENT_HOVER } from '../../products/catalogGradients';
import ClientManagementFilters from './ClientManagementFilters';
import ClientManagementTable from './ClientManagementTable';
import ClientCreateDrawer from './ClientCreateDrawer';
import type { ClientDrawerFormSeed } from './ClientCreateDrawer';
import { DeleteClientConfirmationModal, DeleteClientResult } from '../../modals/confirmation';
import { salesService } from '../../../services/sales.service';
import type { CustomerProfile } from '../../../types/sales.types';
import { parseCustomerApiErrorMessage } from '../../../utils/customerApiMessages';
import { downloadClientsCsv } from '../../../utils/crmCustomerExport';
import type { ClientManagementFiltersState, ClientManagementRow } from './types';
import { clearCrmClientDrawerRestore, readCrmClientDrawerRestore } from './crmClientDrawerRestore';
import { useCrmClientPermissions } from '../../../hooks/useCrmClientPermissions';

const emptyFilters = (): ClientManagementFiltersState => ({
  searchTerm: '',
  email: '',
  phone: '',
  loyaltyTier: '',
  saleDateFrom: '',
  saleDateTo: '',
});

const mapCustomerToRow = (c: CustomerProfile): ClientManagementRow => ({
  id: c.id,
  customerName: c.customerName,
  identityDocumentNumber: c.identityDocumentNumber ?? null,
  customerEmail: c.customerEmail ?? null,
  customerPhone: c.customerPhone ?? null,
  referenceNumber1: c.referenceNumber1 ?? null,
  referenceNumber2: c.referenceNumber2 ?? null,
  loyaltyTier: c.loyaltyTier,
  totalPurchases: c.totalPurchases,
  purchaseCount: c.purchaseCount,
  marketingConsent: c.marketingConsent,
  customerAddress: c.customerAddress,
  dateOfBirth: c.dateOfBirth ? c.dateOfBirth.toISOString().slice(0, 10) : undefined,
  gender: c.gender,
  preferredPaymentMethod: c.preferredPaymentMethod,
  customerNotes: c.customerNotes,
  referralSource: c.referralSource,
});

const customerProfileToDrawerSeed = (c: CustomerProfile): ClientDrawerFormSeed =>
  ({
    id: c.id,
    customerName: c.customerName,
    identityDocumentNumber: c.identityDocumentNumber ?? '',
    customerEmail: c.customerEmail ?? '',
    customerPhone: c.customerPhone ?? '',
    referenceNumber1: c.referenceNumber1 ?? '',
    referenceNumber2: c.referenceNumber2 ?? '',
    customerAddress: c.customerAddress ?? '',
    dateOfBirth: c.dateOfBirth ? c.dateOfBirth.toISOString().slice(0, 10) : '',
    customerNotes: c.customerNotes ?? '',
    gender: c.gender ?? '',
    preferredPaymentMethod: c.preferredPaymentMethod ?? '',
    loyaltyTier: c.loyaltyTier,
    marketingConsent: c.marketingConsent,
    referralSource: c.referralSource ?? '',
  }) as ClientDrawerFormSeed;

const ClientManagementView: React.FC = () => {
  const { canAdd } = useCrmClientPermissions();
  const [clientsData, setClientsData] = useState<ClientManagementRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [exportBusy, setExportBusy] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [draftFilters, setDraftFilters] = useState<ClientManagementFiltersState>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<ClientManagementFiltersState>(emptyFilters);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
  });
  const [actionNotice, setActionNotice] = useState<{
    open: boolean;
    message: string;
    severity: 'info' | 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');
  const [drawerSeed, setDrawerSeed] = useState<ClientDrawerFormSeed | undefined>(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [clientsToDelete, setClientsToDelete] = useState<ClientManagementRow[]>([]);

  const loadClients = useCallback(
    async (opts?: { page?: number }) => {
      setListError(null);
      setListLoading(true);
      try {
        const page = opts?.page ?? pagination.currentPage;
        const searchTerm = appliedFilters.searchTerm.trim();
        const email = appliedFilters.email.trim();
        const phone = appliedFilters.phone.trim();
        const saleDateFrom = appliedFilters.saleDateFrom.trim();
        const saleDateTo = appliedFilters.saleDateTo.trim();
        const res = await salesService.getCustomersPaginated({
          page,
          limit: pagination.itemsPerPage,
          searchTerm: searchTerm !== '' ? searchTerm : undefined,
          email: email !== '' ? email : undefined,
          phone: phone !== '' ? phone : undefined,
          loyaltyTier: appliedFilters.loyaltyTier !== '' ? appliedFilters.loyaltyTier : undefined,
          saleDateFrom: saleDateFrom !== '' ? saleDateFrom : undefined,
          saleDateTo: saleDateTo !== '' ? saleDateTo : undefined,
        });
        setClientsData(res.data.map(mapCustomerToRow));
        setTotalCount(res.total);
        if (opts?.page != null) {
          setPagination((p) => ({ ...p, currentPage: opts.page! }));
        }
      } catch (e) {
        setListError(parseCustomerApiErrorMessage(e));
      } finally {
        setListLoading(false);
      }
    },
    [pagination.currentPage, pagination.itemsPerPage, appliedFilters],
  );

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  const totalItems = totalCount;
  const totalPages = Math.max(1, Math.ceil(totalCount / pagination.itemsPerPage));

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(totalCount / pagination.itemsPerPage));
    if (pagination.currentPage > tp) {
      setPagination((p) => ({ ...p, currentPage: tp }));
    }
  }, [totalCount, pagination.itemsPerPage, pagination.currentPage]);

  const handleSearch = useCallback(() => {
    setAppliedFilters(draftFilters);
    setPagination((p) => ({ ...p, currentPage: 1 }));
    setSelectedClients([]);
  }, [draftFilters]);

  const handleReset = useCallback(() => {
    setDraftFilters(emptyFilters());
    setAppliedFilters(emptyFilters());
    setPagination((p) => ({ ...p, currentPage: 1 }));
    setSelectedClients([]);
  }, []);

  const handleClearAppliedField = useCallback((field: keyof ClientManagementFiltersState) => {
    setDraftFilters((prev) => ({ ...prev, [field]: '' }));
    setAppliedFilters((prev) => ({ ...prev, [field]: '' }));
    setPagination((p) => ({ ...p, currentPage: 1 }));
    setSelectedClients([]);
  }, []);

  const handlePageChange = (page: number) => {
    setPagination((p) => ({ ...p, currentPage: page }));
    setSelectedClients([]);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<unknown>) => {
    setPagination({
      currentPage: 1,
      itemsPerPage: Number(event.target.value),
    });
    setSelectedClients([]);
  };

  const handleAdd = () => {
    setDrawerMode('create');
    setDrawerSeed(undefined);
    setCreateDrawerOpen(true);
  };

  const handleExport = useCallback(async () => {
    setExportBusy(true);
    try {
      const searchTerm = appliedFilters.searchTerm.trim();
      const email = appliedFilters.email.trim();
      const phone = appliedFilters.phone.trim();
      const saleDateFrom = appliedFilters.saleDateFrom.trim();
      const saleDateTo = appliedFilters.saleDateTo.trim();
      const res = await salesService.getCustomersPaginated({
        page: 1,
        limit: 10000,
        searchTerm: searchTerm !== '' ? searchTerm : undefined,
        email: email !== '' ? email : undefined,
        phone: phone !== '' ? phone : undefined,
        loyaltyTier: appliedFilters.loyaltyTier !== '' ? appliedFilters.loyaltyTier : undefined,
        saleDateFrom: saleDateFrom !== '' ? saleDateFrom : undefined,
        saleDateTo: saleDateTo !== '' ? saleDateTo : undefined,
      });
      const rows = res.data.map(mapCustomerToRow);
      downloadClientsCsv(rows, `clientes_${new Date().toISOString().slice(0, 10)}.csv`);
      setActionNotice({
        open: true,
        severity: 'success',
        message:
          rows.length === 1
            ? 'Se exportó 1 cliente.'
            : `Se exportaron ${rows.length} clientes (filtros aplicados).`,
      });
    } catch (e) {
      setActionNotice({
        open: true,
        severity: 'error',
        message: parseCustomerApiErrorMessage(e),
      });
    } finally {
      setExportBusy(false);
    }
  }, [appliedFilters]);

  const handleDeleteSelected = () => {
    if (selectedClients.length === 0) return;
    const targets = clientsData.filter((c) => selectedClients.includes(c.id));
    setClientsToDelete(targets);
    setDeleteModalOpen(true);
  };

  const findClientById = useCallback(
    (id: number) => clientsData.find((c) => c.id === id) ?? null,
    [clientsData],
  );

  const openClientDetailById = useCallback(async (id: number) => {
    try {
      const c = await salesService.getCustomerById(id);
      setDrawerSeed(customerProfileToDrawerSeed(c));
      setDrawerMode('view');
      setCreateDrawerOpen(true);
    } catch (e) {
      setActionNotice({
        open: true,
        severity: 'error',
        message:
          e instanceof Error ? e.message : 'No se pudo cargar el cliente para mostrar el detalle.',
      });
    }
  }, []);

  const handleViewClient = useCallback(
    async (id: number) => {
      if (!findClientById(id)) return;
      await openClientDetailById(id);
    },
    [findClientById, openClientDetailById],
  );

  useEffect(() => {
    const restore = readCrmClientDrawerRestore();
    if (restore == null) return;
    clearCrmClientDrawerRestore();
    void openClientDetailById(restore.clientId);
  }, [openClientDetailById]);
  const handleEditClient = useCallback(
    async (id: number) => {
      if (!findClientById(id)) return;
      try {
        const c = await salesService.getCustomerById(id);
        setDrawerSeed(customerProfileToDrawerSeed(c));
        setDrawerMode('edit');
        setCreateDrawerOpen(true);
      } catch (e) {
        setActionNotice({
          open: true,
          severity: 'error',
          message: e instanceof Error ? e.message : 'No se pudo cargar el cliente para editar.',
        });
      }
    },
    [findClientById],
  );
  const handleDeleteClient = useCallback(
    (id: number) => {
      const target = findClientById(id);
      if (!target) return;
      setClientsToDelete([target]);
      setDeleteModalOpen(true);
    },
    [findClientById],
  );

  const handleClientCreated = useCallback(() => {
    setActionNotice({
      open: true,
      severity: 'success',
      message: 'Cliente creado con éxito.',
    });
    void loadClients({ page: 1 });
  }, [loadClients]);

  const handleClientUpdated = useCallback(() => {
    setActionNotice({
      open: true,
      severity: 'success',
      message: 'Cliente actualizado con éxito.',
    });
    void loadClients();
  }, [loadClients]);

  const handleConfirmDelete = useCallback(async (): Promise<DeleteClientResult[]> => {
    const results: DeleteClientResult[] = [];
    for (const c of clientsToDelete) {
      try {
        await salesService.deleteCustomer(c.id);
        results.push({ clientId: c.id, success: true });
      } catch {
        results.push({ clientId: c.id, success: false });
      }
    }
    const removedIds = new Set(results.filter((r) => r.success).map((r) => r.clientId));
    setSelectedClients((prev) => prev.filter((id) => !removedIds.has(id)));
    void loadClients();
    return results;
  }, [clientsToDelete, loadClients]);

  const handleDeleteComplete = useCallback((results: DeleteClientResult[]) => {
    const deletedCount = results.filter((r) => r.success).length;
    const failedCount = results.filter((r) => !r.success).length;

    if (failedCount === 0) {
      setActionNotice({
        open: true,
        severity: 'success',
        message:
          deletedCount === 1
            ? 'Cliente eliminado con éxito.'
            : `${deletedCount} clientes eliminados con éxito.`,
      });
      return;
    }

    if (deletedCount === 0) {
      setActionNotice({
        open: true,
        severity: 'error',
        message:
          failedCount === 1
            ? 'No se pudo eliminar el cliente. Puede tener pedidos activos o no tener permisos.'
            : `No se pudieron eliminar ${failedCount} clientes. Revisa pedidos activos o permisos.`,
      });
      return;
    }

    setActionNotice({
      open: true,
      severity: 'info',
      message: `Se eliminaron ${deletedCount} cliente(s). ${failedCount} no se pudieron eliminar (p. ej. pedidos activos).`,
    });
  }, []);

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageToolbar>
          {canAdd && (
            <PrimaryButton
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={handleAdd}
              sx={{
                background: 'linear-gradient(to right, #06b6d4, #10b981)',
                color: 'white',
                '&:hover': { background: 'linear-gradient(to right, #0891b2, #059669)' },
              }}
            >
              Añadir
            </PrimaryButton>
          )}

          <SecondaryButton
            variant="outlined"
            startIcon={<DownloadIcon />}
            size="small"
            onClick={() => void handleExport()}
            disabled={exportBusy || listLoading}
            sx={(theme) => ({
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.primary,
                borderColor: theme.palette.text.secondary,
              },
            })}
          >
            {exportBusy ? 'Exportando…' : 'Exportar'}
          </SecondaryButton>

          <DangerButton
            variant="contained"
            startIcon={<DeleteIcon />}
            size="small"
            onClick={handleDeleteSelected}
            disabled={selectedClients.length === 0}
            sx={{
              background: CATALOG_CTA_GRADIENT,
              color: '#ffffff',
              border: 'none',
              boxShadow: '0 2px 10px rgba(45, 99, 237, 0.35)',
              '&:hover': {
                background: CATALOG_CTA_GRADIENT_HOVER,
                boxShadow: '0 4px 14px rgba(45, 99, 237, 0.4)',
              },
              '&.Mui-disabled': {
                color: 'rgba(255,255,255,0.85)',
                opacity: 0.55,
              },
            }}
          >
            Eliminar
          </DangerButton>
        </ListPageToolbar>

        {listError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void loadClients()}>
                Reintentar
              </Button>
            }
          >
            {listError}
          </Alert>
        )}

        <ListPageFiltersSection>
          <ClientManagementFilters
            filters={draftFilters}
            setFilters={setDraftFilters}
            appliedFilters={appliedFilters}
            onClearAppliedField={handleClearAppliedField}
            showMoreFilters={showMoreFilters}
            onSearch={handleSearch}
            onReset={handleReset}
            onToggleMoreFilters={() => setShowMoreFilters((v) => !v)}
          />
        </ListPageFiltersSection>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
            border: 'none',
            bgcolor: 'transparent',
            overflow: 'visible',
            minHeight: 0,
          }}
        >
          {listLoading ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                minHeight: 280,
              }}
            >
              <CircularProgress size={36} />
              <Typography variant="body2" color="text.secondary">
                Cargando clientes…
              </Typography>
            </Box>
          ) : (
            <ClientManagementTable
              clients={clientsData}
              selectedClients={selectedClients}
              setSelectedClients={setSelectedClients}
              onViewClient={handleViewClient}
              onEditClient={handleEditClient}
              onDeleteClient={handleDeleteClient}
            />
          )}
        </Box>

        {totalItems > 0 && (
          <Box>
            <CommonPagination
              currentPage={Math.min(pagination.currentPage, totalPages)}
              itemsPerPage={pagination.itemsPerPage}
              totalItems={totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>
        )}
      </ListPageMain>

      <ClientCreateDrawer
        open={createDrawerOpen}
        mode={drawerMode}
        initialData={drawerMode === 'create' ? undefined : drawerSeed}
        onClose={() => {
          clearCrmClientDrawerRestore();
          setCreateDrawerOpen(false);
          setDrawerSeed(undefined);
        }}
        onCreated={handleClientCreated}
        onUpdated={handleClientUpdated}
      />

      <DeleteClientConfirmationModal
        open={deleteModalOpen}
        clients={clientsToDelete}
        onClose={() => {
          setDeleteModalOpen(false);
          setClientsToDelete([]);
        }}
        onConfirm={handleConfirmDelete}
        onDeleteComplete={handleDeleteComplete}
      />

      <StyledSnackbar
        open={actionNotice.open}
        autoHideDuration={4000}
        onClose={() => setActionNotice((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <StyledAlert
          onClose={() => setActionNotice((n) => ({ ...n, open: false }))}
          severity={actionNotice.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {actionNotice.message}
        </StyledAlert>
      </StyledSnackbar>
    </ListPageScaffold>
  );
};

export default ClientManagementView;
